import { useI18n } from 'vue-i18n'
import { format as dateFnsFormat, parse as dateFnsParse, isValid as dateFnsIsValid, isWithinInterval as dateFnsIsWithinInterval, subYears as dateFnsSubYears, addYears as dateFnsAddYears } from 'date-fns'
// FIXME: 現在の言語情報を取得してから言語情報を取得するようにしたい
import { ja, enUS } from 'date-fns/locale'
import { formatInTimeZone } from 'date-fns-tz'

import type { WritableComputedRef } from 'vue'

import { toDateTime } from '@/utils/date'

/**
 * Types
 */
export type Format = (
  date: Date | string,
  formatStr?: string,
  options?: {
    locale?: Locale
    weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6
    firstWeekContainsDate?: number
    useAdditionalWeekYearTokens?: boolean
    useAdditionalDayOfYearTokens?: boolean
  }
) => string
type DateFnsParse = typeof dateFnsParse
type Parse = (
  dateStr: Parameters<DateFnsParse>[0] | string,
  formatStr?: Parameters<DateFnsParse>[1],
  date?: Parameters<DateFnsParse>[2],
  options?: Parameters<DateFnsParse>[3]
) => ReturnType<DateFnsParse>
type FormatInTimeZone = typeof formatInTimeZone
type FormatDateTime = (
  date: Parameters<FormatInTimeZone>[0],
  formatStr?: Parameters<FormatInTimeZone>[2],
  timeZone?: Parameters<FormatInTimeZone>[1],
  options?: Parameters<FormatInTimeZone>[3]
) => ReturnType<FormatInTimeZone>
type IsValid = (date: any) => boolean
type DateFnsIsWithinInterval = typeof dateFnsIsWithinInterval
type IsWithinInterval = (date: Parameters<DateFnsIsWithinInterval>[0], interval: Parameters<DateFnsIsWithinInterval>[1]) => boolean
type DateFnsSubYears = typeof dateFnsSubYears
type SubYears = (date: Parameters<DateFnsSubYears>[0], years: Parameters<DateFnsSubYears>[1]) => ReturnType<DateFnsSubYears>
type DateFnsAddYears = typeof dateFnsAddYears
type AddYears = (date: Parameters<DateFnsAddYears>[0], years: Parameters<DateFnsAddYears>[1]) => ReturnType<DateFnsAddYears>

/**
 * 言語データ
 */
const locales = { ja, en: enUS } as const

/**
 * 言語データのキーであるか
 */
const isLocalesKey = (value: string): value is keyof typeof locales => {
  return Object.hasOwn(locales, value)
}

/**
 * 現在の言語データを取得する
 * @param locale 現在の言語情報
 * @returns 現在の言語データ
 */
const getLocaleFactory = (locale: WritableComputedRef<string>) => () => {
  const _locale = locale?.value
  const currentLocaleStr = isLocalesKey(_locale) ? _locale : 'ja'
  return locales[currentLocaleStr]
}

/**
 * コンポジション関数
 */
type UseDate = () => {
  formatDate: Format
  formatDateTime: FormatDateTime
  parseDate: Parse
  isValid: IsValid
  isWithinInterval: IsWithinInterval
  subYears: SubYears
  addYears: AddYears
}

export const useDate: UseDate = () => {
  const { locale, t } = useI18n()
  const getLocale = getLocaleFactory(locale)

  const format: Format = (date, formatStr, options) => {
    try {
      return dateFnsFormat(toDateTime(date), formatStr!, {
        locale: getLocale(),
        ...options,
      })
    }
    catch {
      return ''
    }
  }

  const parse: Parse = (dateStr, formatStr, date, options) => {
    try {
      return dateFnsParse(dateStr, formatStr!, date!, {
        locale: getLocale(),
        ...options,
      })
    }
    catch {
      return new Date(NaN)
    }
  }

  /**
   * 指定したタイムゾーンに合わせた日時のフォーマットを返す
   *
   * 例) 2025-01-01T00:00:00.000Z のdateの場合
   * - timeZone: 'UTC' = 2025/1/1 00:00
   * - timeZone: 'Asia/Tokyo' = 2025/1/1 09:00
   */
  const formatTZ: FormatDateTime = (date, formatStr, timeZone, options) => {
    try {
      return formatInTimeZone(date, timeZone!, formatStr!, {
        locale: getLocale(),
        ...options,
      })
    }
    catch {
      return ''
    }
  }

  const isValid: IsValid = (date) => {
    return dateFnsIsValid(date)
  }

  const isWithinInterval: IsWithinInterval = (date, interval) => {
    return dateFnsIsWithinInterval(date, interval)
  }

  const subYears: SubYears = (date, years) => {
    return dateFnsSubYears(date, years)
  }

  const addYears: AddYears = (date, years) => {
    return dateFnsAddYears(date, years)
  }

  return {
    formatDate: (date, formatStr, options) =>
      format(date, formatStr || t('common.date.dateFormatTemp'), options),
    formatDateTime: (
      date,
      formatStr,
      timeZone = 'UTC',
      options,
    ) => formatTZ(date, formatStr || t('common.date.dateTimeFormatTemp'), timeZone, options),
    parseDate: (dateStr, formatStr, date, options) =>
      parse(dateStr, formatStr || t('common.date.dateFormatTemp'), date || new Date(), options),
    isValid,
    isWithinInterval,
    subYears,
    addYears,
  }
}
